import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
// assets
//
import { LinearProgress } from '@mui/material';
import { useTranslate } from 'src/locales';

import Iconify from '../iconify';
import RejectionFiles from './errors-rejection-files';
import MultiFilePreview from './preview-multi-file';
import SingleFilePreview from './preview-single-file';
//
import { UploadProps } from './types';

// ----------------------------------------------------------------------

export default function Upload({
  disabled,
  multiple = false,
  error,
  helperText,
  isUploading,
  file,
  onDelete,
  //
  files,
  maxFile,
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  sx,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    ...other,
  });
  const { t: tConnect } = useTranslate('connect');

  const hasFile = !!file && !multiple;

  const hasFiles = !!files && multiple && !!files.length;

  const hasError = isDragReject || !!error;

  const renderPlaceholder = (
    <Stack spacing={1} direction="column" alignItems="center" justifyContent="center">
      <Iconify
        icon={hasError ? 'icon-park-outline:error-picture' : 'icon-park-outline:add-picture'}
        width={25}
      />
      <Stack spacing={1} sx={{ textAlign: 'center' }}>
        <Typography variant="h6">{tConnect('profile.upload-picture.uploader')}</Typography>
      </Stack>
    </Stack>
  );

  const renderSinglePreview = (
    <SingleFilePreview imgUrl={typeof file === 'string' ? file : file?.preview} />
  );

  const removeSinglePreview = hasFile && onDelete && (
    <IconButton
      size="small"
      onClick={onDelete}
      sx={{
        top: 16,
        right: 16,
        zIndex: 9,
        position: 'absolute',
        color: (theme) => alpha(theme.palette.common.white, 0.8),
        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
        },
      }}
    >
      <Iconify icon="mingcute:close-line" width={18} />
    </IconButton>
  );
  console.log('files', files);
  const renderMultiPreview = hasFiles && (
    <Box sx={{ my: 1 }}>
      <MultiFilePreview
        files={files.filter((response) => response !== null)}
        thumbnail={thumbnail}
        onRemove={onRemove}
      />
    </Box>
  );

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      {renderMultiPreview}
      {files?.length === maxFile ? null : (
        <Box
          {...getRootProps()}
          sx={{
            p: 3,
            outline: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
            border: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
            transition: (theme) => theme.transitions.create(['opacity', 'padding']),
            '&:hover': {
              opacity: 0.72,
            },
            ...(isDragActive && {
              opacity: 0.72,
            }),
            ...(disabled && {
              opacity: 0.48,
              pointerEvents: 'none',
            }),
            ...(hasError && {
              color: 'error.main',
              borderColor: 'error.main',
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            }),
            ...(hasFile && {
              padding: '24% 0',
            }),
          }}
        >
          <input {...getInputProps()} />
          {hasFile && renderSinglePreview}
          {!hasFile && isUploading && <LinearProgress />}
          {!hasFile && !isUploading && renderPlaceholder}
        </Box>
      )}

      {removeSinglePreview}

      {helperText && helperText}

      <RejectionFiles fileRejections={fileRejections} />
    </Box>
  );
}
